<template>
  <AuthLayout :source="source">
    <div class="flex flex-col gap-6">
      <div class="text-3xl">
        Login to the
        <br />
        {{ loginPortalName }}
      </div>
      <Alert
        v-if="failedSso"
        alert-type="warn"
        :text="`Couldn't find an account from that ${failedSso} profile`"
      />
      <Alert
        v-if="successfulPasswordReset"
        alert-type="info"
        :text="`Your password has been changed successfully. You can now login with your new password.`"
      />
      <Alert
        v-if="successfulInvitationAcceptance"
        alert-type="info"
        text="Your account has been created successfully, please sign in to access the Portal."
      />
      <div v-show="currentScreen === Screen.Login">
        <FormKit
          v-show="currentScreen === Screen.Login"
          id="login-form"
          v-bind="$attrs"
          v-model="loginData"
          type="form"
          :actions="false"
          :incomplete-message="false"
          :errors="formErrors"
          @submit="onLogin"
        >
          <div>
            <div class="flex flex-col gap-6">
              <FormKit
                id="email-input"
                name="email"
                type="email"
                autocomplete="email"
                validation-label="Email"
                validation="required|length:0,80|email"
                label="Email"
                placeholder="name@email.com"
              />
              <FormKit
                type="toggleablePassword"
                error-behavior="submit"
                name="password"
                label="Password"
                validation=""
                placeholder="********"
              />

              <FormKitMessages />

              <div class="flex flex-col gap-2">
                <LoadingButton
                  type="submit"
                  class="btn btn-primary w-full flex gap-2 items-center"
                  :args="{ showSpinner: showSpinnerInSubmitButton }"
                  :disabled="
                    loginData.email === '' || loginData.password === ''
                  "
                >
                  Login with password
                  <ArrowRightIcon class="mt-[2px] h-6 w-6" />
                </LoadingButton>
                <LoadingButton
                  type="submit"
                  class="btn btn-tertiary w-full flex gap-2 items-center"
                  :args="{ callback: requestMagicLink }"
                >
                  <img class="mt-[2px]" src="@/assets/images/magic-link.svg" />
                  Get a link instead
                </LoadingButton>
                <div class="text-right">
                  <NuxtLink
                    class="link-primary text-sm"
                    :to="`/auth/reset-password-request`"
                  >
                    Reset Password
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>
        </FormKit>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-center mt-8">
            <div class="flex-1 border-t border-fp-gray-2"></div>
            <div class="mx-4 text-fp-green-7">or login with</div>
            <div class="flex-1 border-t border-fp-gray-2"></div>
          </div>

          <section class="flex flex-col gap-5 items-center">
            <div class="flex flex-col md:flex-row gap-2">
              <button
                type="button"
                class="btn btn-tertiary ml-2 text-fp-gray-5 w-[12rem] h-[3rem] flex gap-2"
                @click="loginWithExternalProvider('google')"
              >
                <img
                  class="h-4 w-4"
                  src="@/assets/images/btn-google.png"
                  alt="Google"
                />
                Google
              </button>

              <button
                type="button"
                class="btn btn-tertiary ml-2 text-fp-gray-5 w-[12rem] h-[3rem] flex gap-2"
                @click="loginWithExternalProvider('linkedin')"
              >
                <img
                  class="h-4 w-4"
                  src="@/assets/images/btn-linkedin.png"
                  alt="Linkedin"
                />
                Linkedin
              </button>
            </div>
          </section>
        </div>
      </div>

      <OtpForm
        v-show="currentScreen === Screen.OTP"
        v-model:otp="loginData.otp"
        v-model:errors="formErrors"
        :callback="onLogin"
        :show-spinner-in-submit-button="showSpinnerInSubmitButton"
        :back-button-action="() => changeScreen(Screen.Login)"
      />
      <div
        v-show="currentScreen === Screen.MagicLinkSuccess"
        class="p-8 bg-white overflow-hidden shadow rounded-lg"
      >
        <div class="flex flex-col gap-4">
          <BackButton @click="() => changeScreen(Screen.Login)" />
          <p>
            We've sent a magic link to your email. Please check your inbox and
            click the link to login.
          </p>
        </div>
      </div>
    </div>
  </AuthLayout>
</template>

<script setup lang="ts">
import { getNode } from '@formkit/core'
import { FormKitMessages } from '@formkit/vue'
import * as Sentry from '@sentry/vue'
import { FetchError } from 'ofetch'
import OtpForm from '~/../member-webapp-shared/components/auth/OtpForm.vue'
import Alert from '~/../member-webapp-shared/components/common/Alert.vue'
import BackButton from '~/../member-webapp-shared/components/common/BackButton.vue'
import LoadingButton from '~/../member-webapp-shared/components/common/LoadingButton.vue'
import ArrowRightIcon from '~/../member-webapp-shared/components/icons/streamline/ArrowRightIcon.vue'
import AuthLayout from '~/../member-webapp-shared/layouts/auth-layout.vue'
import { AuthErrorEnum } from '~/../member-webapp-shared/models/auth-error-enum'
import { getSafeRedirectPath } from '~/../member-webapp-shared/services/safe-redirect'

const props = defineProps<{
  signInFunction: (
    credentials: {
      email: string
      password: string
      otp: string
    },
    options: {
      redirect: boolean
    }
  ) => Promise<Record<string, unknown> | undefined>
  resendConfirmationEmail?: (email: string) => Promise<Response>
  source: 'admin' | 'member' | 'grantee'
}>()

const loginPortalName = computed(() => {
  if (props.source === 'admin') return 'FP Navigator'
  if (props.source === 'grantee') return 'Grantee Portal'
  return 'Founders Pledge Web App'
})

useHead(() => ({
  title: 'Login',
}))

const route = useRoute()

const showSpinnerInSubmitButton = ref(false)

const loginData = ref<{
  email: string
  password: string
  otp: string
}>({
  email: '',
  password: '',
  otp: '',
})

enum Screen {
  Login,
  OTP,
  MagicLinkSuccess,
}

const currentScreen = ref<Screen>(Screen.Login)
const formErrors = ref<string[]>([])

onMounted(() => {
  if (route.query.error_message) {
    const rawMessage = route.query.error_message as string
    const cleanMessage = rawMessage.replace(/\+/g, ' ')

    formErrors.value = [cleanMessage]
  }
})

const onLogin = async () => {
  showSpinnerInSubmitButton.value = true

  const credentials = {
    email: loginData.value.email,
    password: loginData.value.password,
    otp: loginData.value.otp,
    source: props.source,
  }

  try {
    await props.signInFunction(credentials, {
      redirect: false,
    })
    const redirect =
      getSafeRedirectPath(route.query.redirect, window.location.origin) +
      (route.hash || '')
    navigateTo(redirect, { external: true })
  } catch (error) {
    showSpinnerInSubmitButton.value = false
    if (error instanceof FetchError) {
      switch (error.data?.error) {
        case AuthErrorEnum.InvalidUsernameOrPassword:
          formErrors.value = ['Invalid username or password']
          return
        case AuthErrorEnum.InvalidOtp:
          formErrors.value = ['Invalid code']
          return
        case AuthErrorEnum.AccountLocked:
          formErrors.value = ['Account locked. Please check your email.']
          return
        case AuthErrorEnum.Archived:
          formErrors.value = ['Account is archived.']
          return
        case AuthErrorEnum.AccountNotConfirmed:
          try {
            await props.resendConfirmationEmail?.(loginData.value.email)
            formErrors.value = [
              props.resendConfirmationEmail
                ? `Account not confirmed. We have sent you a new confirmation email to ${loginData.value.email}.`
                : `Account not confirmed.`,
            ]
          } catch (error) {
            Sentry.captureException(error)
            formErrors.value = [`Account not confirmed.`]
          }

          return
        case AuthErrorEnum.TwoFactorRequired:
          changeScreen(Screen.OTP)
          return
      }
    }
    formErrors.value = ['Please try again later']
    Sentry.captureException(error)
  }
}

const requestMagicLink = async () => {
  const emailInput = getNode('email-input')
  if (!emailInput || !emailInput.context?.state.valid) {
    return
  }

  const safeRedirectPath = getSafeRedirectPath(
    route.query.redirect,
    window.location.origin
  )
  const redirectQuery =
    safeRedirectPath !== '/'
      ? `&redirect_url=${encodeURIComponent(safeRedirectPath)}`
      : ''

  try {
    const response = await fetch(
      `${useRuntimeConfig().public.authBaseURL}/users/api/magic-link?source=${
        props.source
      }${redirectQuery}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: loginData.value.email }),
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.error || 'Failed to send magic link'
      formErrors.value = [errorMessage]
      return
    }

    changeScreen(Screen.MagicLinkSuccess)
  } catch (error) {
    console.error('Magic link request failed:', error)
    formErrors.value = [
      'Network error. Please check your connection and try again.',
    ]
  }
}

const loginWithExternalProvider = async (provider: string) => {
  navigateTo(
    `${useRuntimeConfig().public.authBaseURL}/login_with_${provider}?source=${
      props.source
    }`,
    { external: true }
  )
}

const changeScreen = (screen: Screen) => {
  formErrors.value = []
  loginData.value.otp = ''
  currentScreen.value = screen
}

const failedSso = computed(() => {
  const queryParam = route.query['sso-user-not-found-for']
  if (!queryParam) {
    return null
  }

  switch (queryParam) {
    case 'google':
      return 'Google'
    case 'linkedin':
      return 'LinkedIn'
    default:
      return queryParam
  }
})

const successfulPasswordReset = computed(() => {
  const queryParam = route.query['password-reset-success']
  return queryParam === 'true'
})

const successfulInvitationAcceptance = computed(() => {
  const queryParam = route.query['invitation-success']
  return queryParam === 'true'
})
</script>
